import React, { useEffect, useRef, useState } from 'react';

/**
 * カスタムカーソル: 素の状態は小さなドット+リング、
 * インタラクティブ要素で拡大、data-cursor属性でラベル表示（"VIEW"等）。
 * fine pointer専用・静止時はrAFを停止する。
 */
export default function CustomCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState('default'); // default | hover | label
  const [label, setLabel] = useState('');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!fine || reduce) return undefined;
    setEnabled(true);
    document.documentElement.classList.add('fx-cursor');

    const s = { x: window.innerWidth / 2, y: window.innerHeight / 2, tx: 0, ty: 0, raf: null };
    s.tx = s.x;
    s.ty = s.y;

    const paint = () => {
      s.x += (s.tx - s.x) * 0.18;
      s.y += (s.ty - s.y) * 0.18;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${s.tx}px, ${s.ty}px, 0)`;
      }
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${s.x}px, ${s.y}px, 0)`;
      }
      if (Math.abs(s.tx - s.x) + Math.abs(s.ty - s.y) > 0.08) {
        s.raf = requestAnimationFrame(paint);
      } else {
        s.raf = null;
      }
    };
    const ensure = () => {
      if (s.raf == null) s.raf = requestAnimationFrame(paint);
    };

    const onMove = (e) => {
      s.tx = e.clientX;
      s.ty = e.clientY;
      setVisible(true);
      ensure();
    };
    const onOver = (e) => {
      const target = e.target instanceof Element ? e.target : null;
      const labelled = target?.closest('[data-cursor]');
      if (labelled) {
        setMode('label');
        setLabel(labelled.getAttribute('data-cursor') || '');
        return;
      }
      const interactive = target?.closest('a, button, [role="button"], input, textarea, select, summary');
      setMode(interactive ? 'hover' : 'default');
      setLabel('');
    };
    const onLeave = () => setVisible(false);
    const onEnter = () => setVisible(true);

    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerover', onOver, { passive: true });
    document.documentElement.addEventListener('pointerleave', onLeave);
    document.documentElement.addEventListener('pointerenter', onEnter);
    return () => {
      document.documentElement.classList.remove('fx-cursor');
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerover', onOver);
      document.documentElement.removeEventListener('pointerleave', onLeave);
      document.documentElement.removeEventListener('pointerenter', onEnter);
      if (s.raf != null) cancelAnimationFrame(s.raf);
    };
  }, []);

  if (!enabled) return null;

  const ringSize =
    mode === 'label' ? 'h-[88px] w-[88px]' : mode === 'hover' ? 'h-11 w-11' : 'h-7 w-7';
  // JOIN（採用系）はセクションのアクセントに関わらず常に緑
  const ringSkin =
    mode === 'label'
      ? `${label === 'JOIN' ? 'bg-accent-green' : 'bg-live'} border-transparent`
      : 'border-white mix-blend-difference bg-transparent';

  return (
    <>
      <div
        ref={ringRef}
        aria-hidden="true"
        className={`pointer-events-none fixed left-0 top-0 z-[400] transition-opacity duration-300 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div
          className={`flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border transition-[width,height,background-color,border-color] duration-300 ease-out ${ringSize} ${ringSkin}`}
        >
          <span
            className={`select-none whitespace-nowrap text-[10px] font-semibold uppercase tracking-label text-[#F6F4EF] transition-opacity duration-200 ${
              mode === 'label' ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {label}
          </span>
        </div>
      </div>
      <div
        ref={dotRef}
        aria-hidden="true"
        className={`pointer-events-none fixed left-0 top-0 z-[401] transition-opacity duration-300 ${
          visible && mode !== 'label' ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-live" />
      </div>
    </>
  );
}
